// src/PhoneNavigation.jsx

import React, { useContext, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { FaBars } from 'react-icons/fa';
import { IoIosCreate } from 'react-icons/io';
import { BsFillLightningFill } from 'react-icons/bs';
import SkullIcon from './SkullIcon';
import Login from './Login';
import Signup from './Signup';

const pill = ({ isActive }) =>
  `flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
    isActive
      ? 'bg-gradient-to-r from-rose-500 to-pink-600 text-white shadow-lg shadow-rose-500/20'
      : 'text-white/60 hover:bg-white/5 hover:text-white'
  }`;

const PhoneNavigation = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [showOverlay, setShowOverlay] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);

  const overlayItems = [
    { label: 'Home', to: '/browse' },
    ...(user
      ? [
          { label: 'Your designs', to: `/userpage/${user.username}` },
          { label: 'Premium', to: '/subscribe' },
          { label: 'Settings & privacy', to: '/settings' },
          { label: 'Help', to: '/support' },
          { label: 'Sign out', action: () => logout(), danger: true },
        ]
      : []),
  ];

  return (
    <>
      {/* Top bar */}
      <div className="fixed left-0 top-0 z-50 flex w-full items-center justify-between border-b border-white/10 bg-gray-900/90 px-3 py-2.5 backdrop-blur-xl">
        <NavLink
          to="/browse"
          className="flex h-9 w-9 items-center justify-center rounded-xl text-rose-500"
          aria-label="Home"
        >
          <SkullIcon size={28} className="translate-y-[1.5px]" />
        </NavLink>

        <div className="flex items-center gap-2">
          <NavLink to="/" end className={pill}>
            <IoIosCreate size={18} />
            <span>Create</span>
          </NavLink>
          {user && (
            <NavLink to={`/userpage/${user.username}`} className={pill}>
              <BsFillLightningFill size={15} />
              <span>Designs</span>
            </NavLink>
          )}
        </div>

        <button
          onClick={() => setShowOverlay(true)}
          className="flex h-9 w-9 items-center justify-center rounded-xl text-white/70 transition-colors hover:bg-white/5 hover:text-white"
          aria-label="Menu"
        >
          <FaBars className="text-lg" />
        </button>
      </div>

      {/* Full-screen overlay */}
      {showOverlay && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-gray-900/95 backdrop-blur-xl">
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <span className="flex items-center gap-2 text-lg font-semibold tracking-tight text-white">
              <SkullIcon size={24} className="translate-y-[2px] text-rose-500" />
              KoiCreate
            </span>
            <button
              className="flex h-9 w-9 items-center justify-center rounded-xl text-2xl text-white/70 transition-colors hover:bg-white/5"
              onClick={() => setShowOverlay(false)}
              aria-label="Close menu"
            >
              ×
            </button>
          </div>

          <nav className="flex flex-1 flex-col justify-center gap-1 px-6">
            {overlayItems.map(({ label, to, action, danger }, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setShowOverlay(false);
                  if (to) navigate(to);
                  else if (action) action();
                }}
                className={`rounded-xl px-4 py-3.5 text-left text-lg transition-colors hover:bg-white/5 ${
                  danger ? 'text-rose-400 hover:text-rose-300' : 'text-white/80 hover:text-white'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>

          {!user && (
            <div className="flex gap-3 px-6 pb-10">
              <button
                className="flex-1 rounded-full border border-white/15 px-4 py-3 text-sm font-semibold text-white/80 transition hover:border-white/30 hover:text-white"
                onClick={() => {
                  setShowOverlay(false);
                  setShowLogin(true);
                }}
              >
                Log in
              </button>
              <button
                className="flex-1 rounded-full bg-gradient-to-r from-rose-500 to-pink-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-rose-500/20 transition hover:brightness-110"
                onClick={() => {
                  setShowOverlay(false);
                  setShowSignup(true);
                }}
              >
                Sign up
              </button>
            </div>
          )}
        </div>
      )}

      {/* Login Modal */}
      {showLogin && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <div className="relative w-full max-w-xs rounded-2xl bg-white p-4 shadow-2xl">
            <button
              className="absolute right-3 top-3 text-gray-500 hover:text-gray-800"
              onClick={() => setShowLogin(false)}
            >
              ✕
            </button>
            <Login onClose={() => setShowLogin(false)} />
          </div>
        </div>
      )}

      {/* Signup Modal */}
      {showSignup && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <div className="relative w-full max-w-xs rounded-2xl bg-white p-4 shadow-2xl">
            <button
              className="absolute right-3 top-3 text-gray-500 hover:text-gray-800"
              onClick={() => setShowSignup(false)}
            >
              ✕
            </button>
            <Signup onClose={() => setShowSignup(false)} />
          </div>
        </div>
      )}
    </>
  );
};

export default PhoneNavigation;
